/**
 * Theme store — Zustand
 *
 * Controls the data-theme attribute on <html>.
 * Persists preference to localStorage under 'ledger-theme'.
 * Preference: 'light' | 'dark' | 'system'
 */

import { create } from 'zustand';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme   = 'light' | 'dark';

/** Return the next compact-control preference without making system a dead end. */
export function nextThemePreference(
  preference: ThemePreference,
  resolved: ResolvedTheme,
): ThemePreference {
  if (preference === 'system') return resolved === 'dark' ? 'light' : 'dark';
  if (preference === 'dark') return 'light';
  return 'system';
}

function resolveTheme(pref: ThemePreference): ResolvedTheme {
  if (pref === 'light') return 'light';
  if (pref === 'dark')  return 'dark';
  // 'system'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme: ResolvedTheme) {
  document.documentElement.setAttribute('data-theme', theme);
}

function storedPreference(): ThemePreference {
  const current = localStorage.getItem('ledger-theme');
  const legacy = localStorage.getItem('theme');
  const stored = current ?? legacy;

  if (stored === 'light' || stored === 'dark' || stored === 'system') {
    if (!current) localStorage.setItem('ledger-theme', stored);
    localStorage.removeItem('theme');
    return stored;
  }

  localStorage.removeItem('theme');
  return 'system';
}

interface ThemeState {
  preference: ThemePreference;
  resolved:   ResolvedTheme;
  setPreference: (pref: ThemePreference) => void;
  /** Called when OS appearance changes (if preference === 'system') */
  syncSystem: () => void;
}

const stored = storedPreference();

export const useThemeStore = create<ThemeState>((set, get) => ({
  preference: stored,
  resolved:   resolveTheme(stored),

  setPreference(pref) {
    localStorage.setItem('ledger-theme', pref);
    const resolved = resolveTheme(pref);
    applyTheme(resolved);
    set({ preference: pref, resolved });
  },

  syncSystem() {
    if (get().preference === 'system') {
      const resolved = resolveTheme('system');
      applyTheme(resolved);
      set({ resolved });
    }
  },
}));

/** Apply the persisted preference before React mounts to avoid a theme flash. */
export function initializeTheme() {
  applyTheme(useThemeStore.getState().resolved);
}

// Listen for OS appearance changes
window
  .matchMedia('(prefers-color-scheme: dark)')
  .addEventListener('change', () => useThemeStore.getState().syncSystem());
